import moment from 'moment-timezone';

/**
 * Get the string to date format required for the API from various date input formats, mapped to UTC using the selected timezone.
 * @param {any} value - The date value to format
 * @param {string} [timezone] - The selected timezone to parse the date in
 * @returns {any} The formatted date string in UTC
 */
export const formatDateWithTimezone = (
  value: string | Date | null | undefined,
  timezone?: string,
): string | null => {
  if (!value) return null;

  if (timezone && typeof value === 'string') {
    // Treat the input string as being in the selected timezone and convert to UTC
    const parsedDate = moment.tz(value, ['YYYY-MM-DD HH:mm:ss', moment.ISO_8601], timezone);
    return parsedDate.isValid() ? parsedDate.utc().toISOString() : null;
  }

  let date;
  if (typeof value === 'string') {
    date = new Date(value);
  } else if (value instanceof Date) {
    date = value;
  }
  if (!date || Number.isNaN(date.getTime())) {
    return null;
  }
  return date.toISOString();
};

/**
 * Convert a local time string to UTC based on the selected timezone
 * @param {string} timeString - The time string (HH:mm)
 * @param {string} timezone - The selected timezone
 * @returns {string} The UTC time string
 */
export const convertTimeToUTC = (timeString: string, timezone: string): string => {
  if (!timeString || !timezone) return timeString;
  const localTime = moment.tz(`1970-01-01 ${timeString}`, 'YYYY-MM-DD HH:mm', timezone);
  return localTime.isValid() ? localTime.utc().format('HH:mm') : timeString;
};

/**
 * Convert a UTC date string to a formatted "YYYY-MM-DD HH:mm:ss" string based on the selected timezone.
 * @param {string} utcString - UTC date string from the API
 * @param {string} timezone - The target timezone
 * @returns {string} The formatted date string
 */
export const convertUTCtoTimezoneDate = (utcString: string, timezone: string): string => {
  if (!utcString || !timezone) return '';
  const date = moment.utc(utcString);
  if (!date.isValid()) return '';
  return date.tz(timezone).format('YYYY-MM-DD HH:mm:ss');
};

/**
 * Convert a UTC time string to a time string based on the selected timezone.
 * @param {string} timeString - UTC time string from the API (HH:mm)
 * @param {string} timezone - The target timezone
 * @returns {string} The formatted time string
 */
export const convertUTCtoTimezoneTime = (timeString: string, timezone: string): string => {
  if (!timeString || !timezone) return timeString;
  const utcTime = moment.utc(`1970-01-01 ${timeString}`, 'YYYY-MM-DD HH:mm');
  return utcTime.isValid() ? utcTime.tz(timezone).format('HH:mm') : timeString;
};
